import React, { useState } from 'react';
import { onboardingConfig } from '../../onboarding/onboardingConfig';
import HelpIcon from './HelpIcon';

const Phase2OnboardingModal = ({ isOpen, onClose, onStepClick, completedSteps = [] }) => {
  const [expandedStep, setExpandedStep] = useState(null);

  if (!isOpen) return null;

  const phase2Steps = onboardingConfig.phase2.checklist;
  const completedCount = phase2Steps.filter(step => completedSteps.includes(step.id)).length;
  const progress = (completedCount / phase2Steps.length) * 100;

  const modalStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
  };

  const contentStyle = {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: window.innerWidth < 768 ? '32px 24px' : '48px',
    maxWidth: '700px',
    width: '90%',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
    animation: 'slideUp 0.5s ease-out',
    position: 'relative',
  };

  const headerStyle = {
    marginBottom: '32px',
    textAlign: 'center',
  };

  const titleStyle = {
    fontSize: window.innerWidth < 768 ? '28px' : '32px',
    fontWeight: '700',
    color: '#003366',
    margin: '0 0 12px 0',
  };

  const subtitleStyle = {
    fontSize: window.innerWidth < 768 ? '14px' : '16px',
    color: '#666',
    margin: '0',
    lineHeight: '1.5',
  };

  const progressContainerStyle = {
    marginBottom: '32px',
  };

  const progressLabelStyle = {
    fontSize: '14px',
    fontWeight: '600',
    color: '#666',
    marginBottom: '8px',
  };

  const progressBarStyle = {
    width: '100%',
    height: '8px',
    backgroundColor: '#e0e0e0',
    borderRadius: '4px',
    overflow: 'hidden',
  };

  const progressFillStyle = {
    height: '100%',
    width: `${progress}%`,
    backgroundColor: '#4CAF50',
    transition: 'width 0.3s ease',
  };

  const stepListStyle = {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  };

  const stepItemStyle = (step) => ({
    marginBottom: '12px',
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
    overflow: 'hidden',
    transition: 'all 0.2s ease',
  });

  const stepHeaderStyle = (isCompleted) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px',
    backgroundColor: isCompleted ? '#e8f5e9' : '#f5f5f5',
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'background-color 0.2s ease',
  });

  const stepTitleContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    flex: 1,
  };

  const checkboxStyle = (isCompleted) => ({
    width: '24px',
    height: '24px',
    borderRadius: '4px',
    backgroundColor: isCompleted ? '#4CAF50' : '#fff',
    border: `2px solid ${isCompleted ? '#4CAF50' : '#ccc'}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontWeight: 'bold',
    fontSize: '14px',
    flexShrink: 0,
  });

  const stepTitleStyle = (isCompleted) => ({
    fontSize: '16px',
    fontWeight: '600',
    color: isCompleted ? '#2e7d32' : '#333',
    margin: 0,
  });

  const stepExpandButtonStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '32px',
    borderRadius: '4px',
    backgroundColor: 'transparent',
    border: 'none',
    cursor: 'pointer',
    color: '#666',
    fontSize: '20px',
    transition: 'all 0.2s ease',
    flexShrink: 0,
  };

  const stepDetailsStyle = {
    padding: '16px',
    backgroundColor: '#fafafa',
    borderTop: '1px solid #e0e0e0',
  };

  const helpTextStyle = {
    fontSize: '14px',
    color: '#666',
    lineHeight: '1.6',
    margin: '0 0 16px 0',
    paddingLeft: '16px',
    borderLeft: '3px solid #2196F3',
  };

  const continueButtonStyle = {
    display: 'block',
    width: '100%',
    padding: '12px 20px',
    marginTop: '24px',
    backgroundColor: '#007bff',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  };

  const closeButtonStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute',
    top: '20px',
    right: '20px',
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: '#f0f0f0',
    border: 'none',
    cursor: 'pointer',
    fontSize: '24px',
    color: '#666',
    transition: 'all 0.2s ease',
  };

  return (
    <div style={modalStyle} onClick={onClose}>
      <div style={contentStyle} onClick={(e) => e.stopPropagation()}>
        <button
          style={closeButtonStyle}
          onClick={onClose}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#e0e0e0';
            e.target.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#f0f0f0';
            e.target.style.transform = 'scale(1)';
          }}
        >
          ✕
        </button>

        <div style={headerStyle}>
          <h2 style={titleStyle}>Phase 2: Advanced Features</h2>
          <p style={subtitleStyle}>
            Master the power features to streamline your workflow and accelerate your business growth.
          </p>
        </div>

        <div style={progressContainerStyle}>
          <div style={progressLabelStyle}>
            Progress: {completedCount} of {phase2Steps.length} steps completed
          </div>
          <div style={progressBarStyle}>
            <div style={progressFillStyle} />
          </div>
        </div>

        <ul style={stepListStyle}>
          {phase2Steps.map((step, index) => {
            const isCompleted = completedSteps.includes(step.id);
            const isExpanded = expandedStep === step.id;

            return (
              <li key={step.id} style={stepItemStyle(step)}>
                <div
                  style={stepHeaderStyle(isCompleted)}
                  onClick={() => setExpandedStep(isExpanded ? null : step.id)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = isCompleted ? '#c8e6c9' : '#eeeeee';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = isCompleted ? '#e8f5e9' : '#f5f5f5';
                  }}
                >
                  <div style={stepTitleContainerStyle}>
                    <div style={checkboxStyle(isCompleted)}>
                      {isCompleted && '✓'}
                    </div>
                    <div>
                      <p style={stepTitleStyle(isCompleted)}>
                        {index + 1}. {step.title}
                      </p>
                    </div>
                    <HelpIcon help={step.help} position="right" />
                  </div>
                  <button
                    style={stepExpandButtonStyle}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#e0e0e0';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                    }}
                  >
                    {isExpanded ? '−' : '+'}
                  </button>
                </div>

                {isExpanded && (
                  <div style={stepDetailsStyle}>
                    <p style={helpTextStyle}>{step.help}</p>
                  </div>
                )}
              </li>
            );
          })}
        </ul>

        <button
          style={continueButtonStyle}
          onClick={onClose}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#0056b3';
            e.target.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#007bff';
            e.target.style.transform = 'translateY(0)';
          }}
        >
          Continue
        </button>

        <style>{`
          @keyframes slideUp {
            from {
              opacity: 0;
              transform: translateY(30px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          /* Scrollbar styling for the modal */
          div::-webkit-scrollbar {
            width: 8px;
          }
          div::-webkit-scrollbar-track {
            background: #f1f1f1;
            border-radius: 4px;
          }
          div::-webkit-scrollbar-thumb {
            background: #888;
            border-radius: 4px;
          }
          div::-webkit-scrollbar-thumb:hover {
            background: #555;
          }
        `}</style>
      </div>
    </div>
  );
};

export default Phase2OnboardingModal;
